import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import Input from "../../../components/ui/inputs/input"
import Button from "../../../components/ui/button/button"
import { GROUP_SIZE_BUCKETS, UNKNOWN_BUCKET, groupByBucket } from "../_utils/buckets"
import { rate, formatRate, gbp } from "../_utils/data"

const clampPercent = (value) => {
	const number = Number(value)
	if (!Number.isFinite(number)) return 0
	return Math.min(100, Math.max(0, number))
}

// Round a percentage to one decimal place for sensible default target values.
const roundRate = (value) => (value == null ? 0 : Math.round(value * 10) / 10)

// Missed-business modeller. For each group-size bucket we know the period's
// de-duped enquiries and actual conversion, plus the trailing six-month average
// margin per booking. A target conversion (defaulting to the actual rate, but
// editable per bucket and via a global box) projects the extra bookings and the
// additional margin they'd represent.
export default function MissedBusiness({ dataset, marginBaseline }) {
	const [targets, setTargets] = useState({})
	const [globalTarget, setGlobalTarget] = useState("")
	const [showBaseline, setShowBaseline] = useState(false)

	const baseRows = useMemo(() => {
		if (!dataset || !marginBaseline) return []

		const enquiriesByBucket = groupByBucket(dataset.finalEnquiries)
		const bookingsByBucket = groupByBucket(dataset.allBookings)

		return GROUP_SIZE_BUCKETS.map((bucket) => {
			const enquiries = enquiriesByBucket[bucket.key].length
			const actualBookings = bookingsByBucket[bucket.key].length
			const baseline = marginBaseline.buckets[bucket.key] || {}

			return {
				key: bucket.key,
				label: bucket.label,
				enquiries,
				actualBookings,
				actualConversion: rate(actualBookings, enquiries),
				avgMarginPerBooking: baseline.avgMarginPerBooking || 0,
				avgMarginPerPerson: baseline.avgMarginPerPerson || 0,
				baselineCount: baseline.count || 0,
			}
		})
	}, [dataset, marginBaseline])

	// Default every target to the bucket's own actual conversion so the table
	// opens neutral (zero missed business) until the user dials a target up.
	useEffect(() => {
		const defaults = {}
		baseRows.forEach((row) => {
			defaults[row.key] = String(roundRate(row.actualConversion))
		})
		setTargets(defaults)
		setGlobalTarget("")
	}, [baseRows])

	const applyGlobal = () => {
		const value = String(clampPercent(globalTarget))
		const next = {}
		baseRows.forEach((row) => {
			next[row.key] = value
		})
		setTargets(next)
	}

	const resetToActual = () => {
		const defaults = {}
		baseRows.forEach((row) => {
			defaults[row.key] = String(roundRate(row.actualConversion))
		})
		setTargets(defaults)
		setGlobalTarget("")
	}

	const rows = useMemo(() => {
		return baseRows.map((row) => {
			const target = clampPercent(targets[row.key] ?? 0)
			const potentialBookings = Math.round((row.enquiries * target) / 100)
			const extraBookings = Math.max(0, potentialBookings - row.actualBookings)
			const missedMargin = extraBookings * row.avgMarginPerBooking
			return { ...row, target, extraBookings, missedMargin }
		})
	}, [baseRows, targets])

	const totals = useMemo(() => {
		return rows.reduce(
			(acc, row) => {
				acc.enquiries += row.enquiries
				acc.actualBookings += row.actualBookings
				acc.extraBookings += row.extraBookings
				acc.missedMargin += row.missedMargin
				return acc
			},
			{ enquiries: 0, actualBookings: 0, extraBookings: 0, missedMargin: 0 }
		)
	}, [rows])

	// The actual 6-month bookings behind each bucket's average margin, so the
	// figures driving the projection are fully auditable.
	const baselineRows = useMemo(() => {
		if (!marginBaseline) return []

		const definitions = [...GROUP_SIZE_BUCKETS]
		if ((marginBaseline.buckets[UNKNOWN_BUCKET.key]?.count || 0) > 0) definitions.push(UNKNOWN_BUCKET)

		return definitions.map((bucket) => {
			const entry = marginBaseline.buckets[bucket.key] || {}
			return {
				key: bucket.key,
				label: bucket.label,
				count: entry.count || 0,
				avgGroupSize: entry.avgGroupSize || 0,
				avgMarginPerBooking: entry.avgMarginPerBooking || 0,
				avgMarginPerPerson: entry.avgMarginPerPerson || 0,
				totalProfit: entry.totalProfit || 0,
			}
		})
	}, [marginBaseline])

	const baselineTotals = useMemo(() => {
		return baselineRows.reduce(
			(acc, row) => {
				acc.count += row.count
				acc.totalProfit += row.totalProfit
				return acc
			},
			{ count: 0, totalProfit: 0 }
		)
	}, [baselineRows])

	if (!rows.length) return null

	const baselineWindow = marginBaseline
		? `${moment(marginBaseline.windowStartMs).format("D MMM YYYY")} - ${moment(marginBaseline.windowEndMs).format("D MMM YYYY")}`
		: ""

	return (
		<div className="missed-business">
			<div className="missed-business-controls">
				<div className="global-target">
					<Input
						type="number"
						label="Target for all groups"
						showLabel={true}
						placeholder="e.g. 40"
						symbol="%"
						value={globalTarget}
						onChange={setGlobalTarget}
					/>
					<Button
						label="Apply to all"
						small={true}
						onClick={applyGlobal}
						disabled={globalTarget === ""}
					/>
					<Button
						label="Reset to actual"
						small={true}
						onClick={resetToActual}
					/>
				</div>
				<p className="baseline-note">
					Margins are the average per booking from the last 6 months of bookings ({baselineWindow}), grouped by the same group sizes. Tweak each group's target conversion to see the additional
					business it would unlock.
				</p>
				<button
					type="button"
					className="baseline-toggle"
					aria-expanded={showBaseline}
					onClick={() => setShowBaseline((value) => !value)}>
					{showBaseline ? "Hide" : "Show"} the 6-month data behind these margins
				</button>
				{showBaseline && (
					<div className="baseline-breakdown">
						<table className="ui-table">
							<thead>
								<tr>
									<th>Group size</th>
									<th>Bookings (6mo)</th>
									<th>Avg group size</th>
									<th>Avg margin / booking</th>
									<th>Avg margin / person</th>
									<th>Total margin</th>
								</tr>
							</thead>
							<tbody>
								{baselineRows.map((row) => (
									<tr key={row.key}>
										<td>{row.label}</td>
										<td>{row.count}</td>
										<td>{row.count ? row.avgGroupSize.toFixed(1) : "-"}</td>
										<td>{row.count ? gbp.format(row.avgMarginPerBooking) : "-"}</td>
										<td>{row.count ? gbp.format(row.avgMarginPerPerson) : "-"}</td>
										<td>{gbp.format(row.totalProfit)}</td>
									</tr>
								))}
								<tr className="totals-row">
									<td>
										<strong>Total</strong>
									</td>
									<td>
										<strong>{baselineTotals.count}</strong>
									</td>
									<td colSpan={3} />
									<td>
										<strong>{gbp.format(baselineTotals.totalProfit)}</strong>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				)}
			</div>

			<div className="missed-business-headline">
				<span className="headline-label">Total additional business at these targets</span>
				<span className="headline-value">{gbp.format(totals.missedMargin)}</span>
				<span className="headline-sub">
					{totals.extraBookings} extra bookings across {totals.enquiries} enquiries
				</span>
			</div>

			<table className="ui-table stat-highlights-table missed-business-table">
				<thead>
					<tr>
						<th>Group size</th>
						<th>Enquiries</th>
						<th>Actual bookings</th>
						<th>Actual conv.</th>
						<th>Avg margin / booking</th>
						<th>Avg margin / person</th>
						<th>Target conv.</th>
						<th>Extra bookings</th>
						<th>Missed business</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row.key}>
							<td>{row.label}</td>
							<td>{row.enquiries}</td>
							<td>{row.actualBookings}</td>
							<td>{formatRate(row.actualConversion)}</td>
							<td>
								{row.baselineCount > 0 ? (
									<>
										{gbp.format(row.avgMarginPerBooking)}
										<small className="baseline-count">{row.baselineCount} bkgs</small>
									</>
								) : (
									<small className="no-baseline">no 6mo data</small>
								)}
							</td>
							<td>{row.baselineCount > 0 ? gbp.format(row.avgMarginPerPerson) : <small className="no-baseline">-</small>}</td>
							<td className="target-cell">
								<Input
									type="number"
									symbol="%"
									wrapperClass="target-input"
									value={targets[row.key] ?? ""}
									onChange={(value) => setTargets((prev) => ({ ...prev, [row.key]: value }))}
								/>
							</td>
							<td>{row.extraBookings}</td>
							<td className="emphasis-cell">{gbp.format(row.missedMargin)}</td>
						</tr>
					))}
					<tr className="totals-row">
						<td>
							<strong>Total</strong>
						</td>
						<td>
							<strong>{totals.enquiries}</strong>
						</td>
						<td>
							<strong>{totals.actualBookings}</strong>
						</td>
						<td colSpan={3} />
						<td />
						<td>
							<strong>{totals.extraBookings}</strong>
						</td>
						<td className="emphasis-cell">
							<strong>{gbp.format(totals.missedMargin)}</strong>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}
